import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Prompt, PromptType, PROMPT_TYPE_KOREAN_NAMES } from "common/models/types";
import { TypeListState } from "pages/Admin/Prompts/promptListState";
import PromptItem from "components/molecules/PromptItem";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import Icon from "components/atoms/Icon";

interface PromptListProps {
  typeCounts: Partial<Record<PromptType, number>>;
  typeLists: Partial<Record<PromptType, TypeListState>>;
  countReliable: boolean;
  countError?: string | null;
  selectedPrompt: Prompt | null;
  onSelectPrompt: (prompt: Prompt) => void;
  onCreatePrompt: () => void;
  onExpandType: (type: PromptType) => void;
  onLoadMoreType: (type: PromptType) => void;
  onRetryType: (type: PromptType) => void;
  onToggleActive?: (promptId: number, isActive: boolean) => void;
}

const PromptListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: white;
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
`;

const ListContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0 0 16px;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 16px;
  text-align: center;
`;

const ErrorBanner = styled.div`
  margin: 8px 12px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: ${COLOR_SYSTEM.get("Yellow")?.[50] ?? "#fff8e1"};
  border: 1px solid ${COLOR_SYSTEM.get("Yellow")?.[200] ?? "#ffe082"};
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  font-size: 12px;
`;

const PromptCard = styled.div`
  margin: 8px 12px;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const AccordionHeader = styled.div<{ hasSelectedItem?: boolean }>`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  cursor: pointer;
  border-bottom: ${({ hasSelectedItem }) =>
    hasSelectedItem ? `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}` : "none"};

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }

  ${({ hasSelectedItem }) =>
    hasSelectedItem &&
    `
    border-left: 3px solid ${COLOR_SYSTEM.get("Blue")?.[500]};
    background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
  `}
`;

const PromptInfo = styled.div`
  flex: 1;
  margin-left: 12px;
`;

const VersionCountBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Skyblue")?.[200]};
  color: ${COLOR_SYSTEM.get("Skyblue")?.[700]};
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 8px;

  &.normal {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
    border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
    color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  }
`;

const AddButton = styled(Button)`
  min-width: 32px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VersionsContainer = styled.div`
  padding: 4px 16px 8px 32px;
`;

const StateMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-size: 12px;
`;

// 펼친 타입의 본문(로딩/에러/항목/더보기)을 그린다.
const TypeBody: React.FC<{
  type: PromptType;
  state?: TypeListState;
  selectedPrompt: Prompt | null;
  onSelectPrompt: (prompt: Prompt) => void;
  onLoadMoreType: (type: PromptType) => void;
  onRetryType: (type: PromptType) => void;
  onToggleActive?: (promptId: number, isActive: boolean) => void;
}> = ({ type, state, selectedPrompt, onSelectPrompt, onLoadMoreType, onRetryType, onToggleActive }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const hasMore = state?.hasMore ?? false;
  const isLoading = state?.isLoading ?? false;
  const items = state?.items ?? [];

  // 무한 스크롤: 아코디언 내부 sentinel 관찰 (P2 해소)
  useEffect(() => {
    if (!hasMore || isLoading || !sentinelRef.current) return;
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMoreType(type);
      },
      { threshold: 0.5 }
    );
    observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, isLoading, type, onLoadMoreType, items.length]);

  // 첫 페이지 로딩
  if (isLoading && items.length === 0) {
    return (
      <LoadMoreContainer>
        <Spinner />
      </LoadMoreContainer>
    );
  }

  // 로드 실패
  if (state?.error && items.length === 0) {
    return (
      <StateMessage>
        불러오기에 실패했습니다.
        <div style={{ marginTop: 8 }}>
          <Button
            buttonType="button"
            color="Secondary"
            size="Small"
            type="Text"
            variant="Outlined"
            onClick={() => onRetryType(type)}
          >
            다시 시도
          </Button>
        </div>
      </StateMessage>
    );
  }

  // 카운트는 있는데 본문이 빈 모순 상황 / 폴백 모드의 빈 타입
  if (!isLoading && items.length === 0) {
    return <StateMessage>항목이 없습니다.</StateMessage>;
  }

  return (
    <VersionsContainer>
      {items.map((prompt) => (
        <PromptItem
          key={prompt.id ?? `tmp-${type}`}
          prompt={prompt}
          isSelected={selectedPrompt?.id === prompt.id}
          onClick={() => onSelectPrompt(prompt)}
          onToggleActive={onToggleActive}
        />
      ))}

      {isLoading && items.length > 0 && (
        <LoadMoreContainer>
          <Spinner />
        </LoadMoreContainer>
      )}

      {/* 무한 스크롤 sentinel + 폴백 "더 보기" 버튼 */}
      {hasMore && !isLoading && (
        <>
          <div ref={sentinelRef} style={{ height: 1 }} />
          <LoadMoreContainer>
            <Button
              buttonType="button"
              color="Secondary"
              size="Small"
              type="Text"
              variant="Outlined"
              onClick={() => onLoadMoreType(type)}
            >
              더 보기
            </Button>
          </LoadMoreContainer>
        </>
      )}
    </VersionsContainer>
  );
};

const PromptList: React.FC<PromptListProps> = ({
  typeCounts,
  typeLists,
  countReliable,
  countError,
  selectedPrompt,
  onSelectPrompt,
  onCreatePrompt,
  onExpandType,
  onLoadMoreType,
  onRetryType,
  onToggleActive,
}) => {
  // 어떤 타입 아코디언이 열려 있는지
  const [openTypes, setOpenTypes] = useState<Partial<Record<PromptType, boolean>>>({});

  // 표시할 종류 목록 (카운트 맵의 키).
  // 폴백 모드에서는 enum 전체를 노출하므로, 펼쳐서 로드했는데 비어 있는 타입은 사후 숨김(계획 4-1).
  const visibleTypes = (Object.keys(typeCounts) as PromptType[]).filter((t) => {
    if (countReliable) return true;
    const s = typeLists[t];
    return !(s && !s.isLoading && !s.error && s.items.length === 0);
  });

  const toggleAccordion = useCallback(
    (type: PromptType) => {
      setOpenTypes((prev) => {
        const next = !prev[type];
        if (next) onExpandType(type); // 펼칠 때만 로드 트리거 (중복은 부모 ref가 가드)
        return { ...prev, [type]: next };
      });
    },
    [onExpandType]
  );

  // 선택된 프롬프트가 속한 타입은 자동으로 펼침 + 로드
  useEffect(() => {
    const t = selectedPrompt?.promptType;
    if (!t) return;
    setOpenTypes((prev) => (prev[t] ? prev : { ...prev, [t]: true }));
    onExpandType(t);
  }, [selectedPrompt?.promptType, onExpandType]);

  return (
    <PromptListContainer>
      <ListHeader>
        <Text textType="Body1" style={{ fontWeight: 600, color: COLOR_SYSTEM.get("Gray")?.[700] }}>
          프롬프트 목록
        </Text>
        <AddButton
          buttonType="button"
          color="Primary"
          variant="Outlined"
          type="Icon"
          size="Small"
          icon="Plus"
          onClick={onCreatePrompt}
        />
      </ListHeader>

      <ListContainer>
        {countError && <ErrorBanner>{countError}</ErrorBanner>}

        {visibleTypes.length === 0 ? (
          <EmptyState>
            <Text textType="H4" style={{ marginBottom: "8px" }}>
              프롬프트가 없습니다
            </Text>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
              새 프롬프트를 추가해보세요.
            </Text>
          </EmptyState>
        ) : (
          visibleTypes.map((type) => {
            const isOpen = openTypes[type] || false;
            const hasSelectedPrompt = selectedPrompt?.promptType === type;
            const count = typeCounts[type] ?? 0;

            return (
              <PromptCard key={type}>
                <AccordionHeader
                  hasSelectedItem={hasSelectedPrompt}
                  onClick={() => toggleAccordion(type)}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <PromptInfo>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Text
                          textType="Body1"
                          style={{
                            fontWeight: 600,
                            color: hasSelectedPrompt
                              ? COLOR_SYSTEM.get("Blue")?.[700]
                              : COLOR_SYSTEM.get("Gray")?.[800],
                          }}
                        >
                          {PROMPT_TYPE_KOREAN_NAMES[type] || type}
                        </Text>
                        {countReliable && (
                          <VersionCountBadge className={!hasSelectedPrompt ? "normal" : ""}>
                            버전 {count}개
                          </VersionCountBadge>
                        )}
                      </div>
                    </PromptInfo>
                  </div>
                  <Icon
                    name={isOpen ? "Chevron Down" : "Chevron Right"}
                    size={16}
                    color={
                      hasSelectedPrompt
                        ? COLOR_SYSTEM.get("Blue")?.[700]
                        : COLOR_SYSTEM.get("Gray")?.[600]
                    }
                  />
                </AccordionHeader>

                {isOpen && (
                  <TypeBody
                    type={type}
                    state={typeLists[type]}
                    selectedPrompt={selectedPrompt}
                    onSelectPrompt={onSelectPrompt}
                    onLoadMoreType={onLoadMoreType}
                    onRetryType={onRetryType}
                    onToggleActive={onToggleActive}
                  />
                )}
              </PromptCard>
            );
          })
        )}
      </ListContainer>
    </PromptListContainer>
  );
};

export default PromptList;
